import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Platform,
} from "react-native";
import * as Haptics from "expo-haptics";
import colors from "../constants/colors";
import { normalize } from "../utils/normalize";
import { JOB_TYPES, DEFAULT_JOB_TYPE_LIST } from "../constants/jobTypes";

const JobTypeFilterTabs = ({
  isDev,
  jobTypeList,
  selectedJobType,
  onJobTypeChange,
  showInternational,
  onInternationalChange,
  internationalFilter,
  internationalStyle,
  tabStyle,
  tabContainerStyle,
  tabItemStyle,
  style,
  hideAll,
  variant = "underline", // "underline", "filled", or "pills"
  jobStats, // New prop for job statistics from API
}) => {
  const [showInternationalJobs, setShowInternationalJobs] = useState(false);

  const jobTypes = jobTypeList ? jobTypeList : JOB_TYPES.map(type => ({
    name: type.name,
    id: type.apiId
  }));

  // Initialize international checkbox state from internationalFilter prop
  useEffect(() => {
    if (internationalFilter) {
      setShowInternationalJobs(
        internationalFilter.type === "international" ||
        (internationalFilter.type === "all" && internationalFilter.countries?.length > 0)
      );
    }
  }, [internationalFilter]);

  // Reset international checkbox when switching to Locum
  useEffect(() => {
    if (selectedJobType === 0) {
      setShowInternationalJobs(false);
    }
  }, [selectedJobType]);

  const handleJobTypePress = (index) => {
    if (Platform.OS === "ios") {
      Haptics.selectionAsync();
    }
    onJobTypeChange(index);
  };

  const handleInternationalToggle = () => {
    if (Platform.OS === "ios") {
      Haptics.selectionAsync();
    }
    const newValue = !showInternationalJobs;
    setShowInternationalJobs(newValue);
    onInternationalChange(newValue);
  };

  // Helper function to get job count for a specific job type
  const getJobCount = (item, index) => {
    if (!jobStats) return 0;
    
    // Handle "All" option
    if (item.id === -1) {
      // Sum all job counts
      let total = 0;
      Object.keys(jobStats).forEach(key => {
        // Exclude draft statuses (-1, -2)
        if (key !== "-1" && key !== "-2") {
          total += jobStats[key] || 0;
        }
      });
      return total;
    }
    
    // Map job type API IDs to job_stats keys
    // job_stats uses: "locum" for 0, and string numbers for others
    if (item.id === 0) {
      return jobStats["locum"] || 0;
    } else {
      return jobStats[String(item.id)] || 0;
    }
  };

  // Badge component
  const renderBadge = (count) => {
    if (!count || count === 0) return null;
    
    return (
      <View
        style={{
          position: 'absolute',
          top: -4,
          right: -8,
          backgroundColor: '#FF3B30',
          borderRadius: 8,
          minWidth: 16,
          height: 16,
          paddingHorizontal: 4,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text
          style={{
            color: 'white',
            fontSize: 10,
            fontWeight: 'bold',
          }}
        >
          {count > 99 ? '99+' : count}
        </Text>
      </View>
    );
  };

  if (!isDev || jobTypeList?.length === 0) {
    return null;
  }

  const renderUnderlineTabs = () => (
    <View style={{ backgroundColor: 'white', borderBottomWidth: 1, borderBottomColor: '#E5E7EB', ...tabContainerStyle }}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 16 }}
      >
        <View style={{ flexDirection: 'row' }}>
          {jobTypes.map((item, index) => {
            if (hideAll && index === 0) return null;
            const isSelected = index === selectedJobType;
            return (
              <TouchableOpacity
                key={item.id}
                onPress={() => handleJobTypePress(index)}
                style={{
                  marginRight: index < jobTypes.length - 1 ? 24 : 0,
                }}
              >
                <View
                  style={{
                    paddingVertical: 12,
                    borderBottomWidth: 3,
                    borderBottomColor: isSelected ? colors.primary : 'transparent',
                    position: 'relative',
                    ...tabItemStyle
                  }}
                >
                  <Text
                    style={{
                      fontSize: normalize(14),
                      fontWeight: isSelected ? '600' : '400',
                      color: isSelected ? colors.primary : '#6B7280',
                    }}
                  >
                    {item.name}
                  </Text>
                  {renderBadge(getJobCount(item, index))}
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );

  const renderFilledTabs = () => (
    <View style={{ backgroundColor: '#F9FAFB', padding: 4, ...tabContainerStyle }}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 4, ...tabStyle }}
      >
        <View style={{ flexDirection: 'row', backgroundColor: '#F3F4F6', borderRadius: 10, padding: 2 }}>
          {jobTypes.map((item, index) => {
            if (hideAll && index === 0) return null;
            const isSelected = index === selectedJobType;
            return (
              <TouchableOpacity
                key={item.id}
                onPress={() => handleJobTypePress(index)}
                style={{
                  marginRight: index < jobTypes.length - 1 ? 2 : 0,
                }}
              >
                <View
                  style={{
                    paddingVertical: 8,
                    paddingHorizontal: 16,
                    backgroundColor: isSelected ? 'white' : 'transparent',
                    borderRadius: 8,
                    minWidth: 80,
                    alignItems: 'center',
                    position: 'relative',
                    shadowColor: isSelected ? '#000' : 'transparent',
                    shadowOffset: { width: 0, height: 1 },
                    shadowOpacity: isSelected ? 0.05 : 0,
                    shadowRadius: 2,
                    elevation: isSelected ? 2 : 0,
                  }}
                >
                  <Text
                    style={{
                      fontSize: normalize(13),
                      fontWeight: isSelected ? '600' : '400',
                      color: isSelected ? '#111827' : '#6B7280',
                    }}
                  >
                    {item.name}
                  </Text>
                  {renderBadge(getJobCount(item, index))}
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );

  const renderPillsTabs = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <View style={{ flexDirection: 'row', marginHorizontal: 12, marginTop: 4, ...tabContainerStyle }}>
        {jobTypes.map((item, index) => {
          if (hideAll && index === 0) return null;
          const isSelected = index === selectedJobType;
          return (
            <TouchableOpacity
              key={item.id}
              onPress={() => handleJobTypePress(index)}
              style={{
                marginLeft: index === 0 ? 0 : 8,
              }}
            >
              <View
                style={{
                  borderRadius: 20,
                  backgroundColor: isSelected ? 'rgb(242, 49, 123)' : '#eaecef',
                  padding: 8,
                  paddingHorizontal: 24,
                  position: 'relative',
                  ...tabStyle,
                }}
              >
                <Text
                  style={{
                    fontSize: normalize(12),
                    fontWeight: 'bold',
                    color: isSelected ? '#fff' : '#333',
                  }}
                >
                  {item.name}
                </Text>
                {renderBadge(getJobCount(item, index))}
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    </ScrollView>
  );

  return (
    <View style={style}>
      {variant === "underline" ? renderUnderlineTabs() :
        variant === "filled" ? renderFilledTabs() :
          renderPillsTabs()}
    </View>
  );
};

export default JobTypeFilterTabs;